import { Badge } from '@givebutter/primitives/badge';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faPartyHorn } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { DateTime } from 'luxon';
import { Ticket } from 'types/domain';

interface TicketEventLocationBadgeProps {
    ticket: Ticket;
}

const getEventData = (ticket: Ticket, finalTimezone: string) => {
    const address = ticket?.event?.address_formatted?.split(',')[0];
    const startAt = ticket.event?.start_at
        ? DateTime.fromISO(ticket.event.start_at).setZone(finalTimezone)
        : null;
    const endAt = ticket.event?.end_at
        ? DateTime.fromISO(ticket.event.end_at).setZone(finalTimezone)
        : null;

    return {
        address,
        month: startAt ? startAt.toFormat('LLL') : '',
        day: startAt ? startAt.toFormat('d') : '',
        startTime: startAt ? startAt.toFormat('cccc t') : '',
        endTime: endAt ? `- ${endAt.toFormat('cccc t')}` : '',
    };
};

const TicketEventDetails = (props: TicketEventLocationBadgeProps) => {
    const { timezone } = useCurrentAccount();
    const { ticket } = props;
    const { data: transaction } = useCurrentTransactionOrById({
        transactionId: ticket.transaction_id,
    });

    const finalTimezone = transaction?.campaign?.timezone || timezone || 'utc';

    const { address, month, day, startTime, endTime } = getEventData(ticket, finalTimezone);

    return (
        <div>
            {startTime ? null : <div className="text-xs text-gray-500">No time found</div>}
            <div className={'bg-gray-25 flex gap-4 rounded-xl px-4 py-2'}>
                <div className="flex items-center">
                    <div className="h-12 w-12 rounded-md border border-gray-200 bg-white pt-1.5">
                        {startTime ? (
                            <div className="flex flex-col items-center">
                                <div>
                                    <p className="text-xs leading-none">{month}</p>
                                </div>
                                <div>
                                    <p className="text-lg font-medium leading-tight text-red-500">
                                        {day}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex h-4/5 items-center justify-center">
                                <FontAwesomeIcon
                                    size={'lg'}
                                    icon={faPartyHorn}
                                    className="text-gray-400"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-auto">
                    <div className="flex h-full flex-col justify-between">
                        <div>
                            <p className="max-w-xs overflow-x-auto text-sm font-medium lg:text-base">
                                {ticket.event?.title}
                            </p>
                        </div>
                        <div className="flex flex-col items-start justify-start gap-2">
                            <div>
                                <p className="max-w-xs items-center overflow-x-auto text-xs text-gray-500 lg:text-sm">
                                    {startTime
                                        ? `${startTime} ${endTime}`
                                        : 'No scheduled time for this event'}
                                </p>
                            </div>
                            {address ? (
                                <div className="max-w-fit">
                                    <Badge color="primary">
                                        <FontAwesomeIcon
                                            size={'sm'}
                                            icon={faLocationDot}
                                            className="text-blue-500"
                                        />
                                        <p className="text-xs text-blue-500 lg:text-sm">
                                            {address}
                                        </p>
                                    </Badge>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TicketEventDetails };
